// src/marketing/ComingSoonPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, ArrowRight, Check } from 'lucide-react';

const ComingSoonPage = ({
  title = 'Coming Soon',
  subtitle = "We're working hard to bring you this feature.",
  featureName = 'This feature',
  estimatedRelease = 'Q3 2025',
}) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // In a real app, you would call an API to store the email
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Success
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      setError('There was an error submitting your email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{title}</h1>
            <p className="text-gray-600 text-lg">{subtitle}</p>
          </div>

          {/* Feature Info */}
          <div className="bg-blue-50 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              What to expect
            </h2>
            <p className="text-gray-700 mb-4">
              {featureName} is currently in development and will be available
              soon. Here's what you can look forward to:
            </p>

            <div className="space-y-3">
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-gray-600">
                  Enhanced management capabilities for all your client projects
                </p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-gray-600">
                  Advanced analytics and reporting features
                </p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-gray-600">
                  Customizable workflows tailored to your business needs
                </p>
              </div>
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-gray-600">
                  Seamless integration with your existing tools
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center">
              <div className="mr-2 px-2.5 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                Estimated Release
              </div>
              <span className="text-gray-700 font-medium">
                {estimatedRelease}
              </span>
            </div>
          </div>

          {/* Notification Form */}
          {!submitted ? (
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-3">
                Get notified when we launch
              </h2>
              <p className="text-gray-600 mb-4">
                Enter your email below and we'll let you know as soon as this
                feature becomes available.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="sr-only">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    'Notify Me'
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className="mb-8 bg-green-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="flex-shrink-0 bg-green-100 rounded-full p-1">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="ml-3 text-lg font-medium text-green-800">
                  Thank you for your interest!
                </h3>
              </div>
              <p className="text-green-700">
                We've added your email to our notification list. We'll let you
                know as soon as this feature becomes available.
              </p>
            </div>
          )}

          {/* Navigation Options */}
          <div className="space-y-4">
            <Link
              to="/"
              className="w-full flex justify-center items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Return to Home
            </Link>

            <Link
              to="/app/dashboard"
              className="w-full flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to Dashboard
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComingSoonPage;
